import { Spinner } from '@frontend/components/common';
import { ICONS, Plus } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { UserSelectV2 } from '@frontend/user/select';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { FormattedMessage } from 'react-intl';

import { ProofOfIdentityCheckbox } from '../../components/proof-of-identity-checkbox.component';
import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useSelectSendersAndReceivers from './select-senders-and-receivers.controller';

const ID = 'select-senders-and-receivers';

const SelectSendersAndReceivers = (props: { wizard_id: string; requireProofOfIdentity?: boolean }) => {
    const viewProps = useSelectSendersAndReceivers(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='flex items-start justify-start self-stretch'>
                <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.PROVIDE_THE_FOLLOWING}</div>
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.selectedSenders.some((sender) => sender !== null) ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.USER className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.SENDERS}</div>
                        </div>
                    </div>
                </div>

                {viewProps.isLoading ? (
                    <Spinner />
                ) : (
                    <div className='flex w-full flex-col gap-3'>
                        {viewProps.selectedSenders.map((senderId, index) => (
                            <div
                                key={index}
                                className='flex w-full items-center gap-3'>
                                <div className='flex-1'>
                                    <UserSelectV2
                                        id={`sender-${index}`}
                                        hideLabel
                                        value={senderId}
                                        onChange={(user) => viewProps.updateSender(index, user)}
                                        isClearable
                                        minSearchCharacters={3}
                                        showSelectedDetails
                                        subset={viewProps.senderSubsets[index]}
                                    />
                                </div>
                                {viewProps.selectedSenders.length > 1 && (
                                    <button
                                        type='button'
                                        onClick={() => viewProps.removeSender(index)}
                                        className='text-foreground-default hover:text-foreground-emphasis shrink-0 transition-colors'
                                        aria-label='Remove sender'>
                                        <ICONS.BUTTON.DELETE className='size-5' />
                                    </button>
                                )}
                            </div>
                        ))}

                        {viewProps.requireProofOfIdentity && (
                            <ProofOfIdentityCheckbox
                                id={`${ID}-drop-off-require-proof-of-identity`}
                                value={viewProps.dropOffRequireProofOfIdentity}
                                onChange={viewProps.setDropOffRequireProofOfIdentity}
                                type='drop-off'
                            />
                        )}

                        <button
                            type='button'
                            onClick={viewProps.addSender}
                            className='flex w-full items-center justify-end gap-3'>
                            <Plus className='size-3.5' />
                            <p className='text-foreground-default text-base leading-normal font-medium'>
                                <FormattedMessage
                                    id='platform.wizard.select_senders.add_another'
                                    description='Button to add another sender in the platform PUDO wizard'
                                    defaultMessage='Add another sender'
                                />
                            </p>
                        </button>
                    </div>
                )}
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.selectedReceivers.some((receiver) => receiver !== null) ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.USER className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}</div>
                        </div>
                    </div>
                </div>

                {viewProps.isLoading ? (
                    <Spinner />
                ) : (
                    <div className='flex w-full flex-col gap-3'>
                        {viewProps.selectedReceivers.map((receiverId, index) => (
                            <div
                                key={index}
                                className='flex w-full items-center gap-3'>
                                <div className='flex-1'>
                                    <UserSelectV2
                                        id={`receiver-${index}`}
                                        hideLabel
                                        value={receiverId}
                                        onChange={(user) => viewProps.updateReceiver(index, user)}
                                        isClearable
                                        minSearchCharacters={3}
                                        showSelectedDetails
                                        subset={viewProps.receiverSubsets[index]}
                                    />
                                </div>
                                {viewProps.selectedReceivers.length > 1 && (
                                    <button
                                        type='button'
                                        onClick={() => viewProps.removeReceiver(index)}
                                        className='text-foreground-default hover:text-foreground-emphasis shrink-0 transition-colors'
                                        aria-label='Remove receiver'>
                                        <ICONS.BUTTON.DELETE className='size-5' />
                                    </button>
                                )}
                            </div>
                        ))}

                        {viewProps.requireProofOfIdentity && (
                            <ProofOfIdentityCheckbox
                                id={`${ID}-pick-up-require-proof-of-identity`}
                                value={viewProps.pickUpRequireProofOfIdentity}
                                onChange={viewProps.setPickUpRequireProofOfIdentity}
                                type='pick-up'
                            />
                        )}

                        <button
                            type='button'
                            onClick={viewProps.addReceiver}
                            className='flex w-full items-center justify-end gap-3'>
                            <Plus className='size-3.5' />
                            <p className='text-foreground-default text-base leading-normal font-medium'>
                                <FormattedMessage
                                    id='platform.wizard.select_receivers.add_another'
                                    description='Button to add another receiver in the platform PUDO wizard'
                                    defaultMessage='Add another receiver'
                                />
                            </p>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectSendersAndReceivers;
